import { Button } from '@/components/Elements/Button/Button';
import { TextInput } from '@/components/Elements/Form/TextInput';
import { Box, Grid, Link, Typography } from '@mui/material';
import { useForm } from 'react-hook-form';
import { LoginFormType } from '@/features/reservation/types/LoginFormType';
import * as yup from 'yup';
import { yupResolver } from '@hookform/resolvers/yup';
import { PasswordInput } from '@/components/Elements/Form/PasswordInput';
import awsconfig from '@/amplifyconfiguration.json';
import { Amplify } from 'aws-amplify';
// import { signInWithRedirect } from 'aws-amplify/auth';
import { useCognitoAuth } from '@/hooks/useCognitoAuth';
import { PageName } from '.';
import { Hub } from 'aws-amplify/utils';
import { useLocation } from 'react-router';
import { useEffect } from 'react';
import { ReservationUrls } from '@/features/reservation/constants/urls';

const pathOrigin = window.location.origin;

const schema: yup.ObjectSchema<Pick<LoginFormType, 'email' | 'password'>> = yup.object({
  email: yup
    .string()
    .required('メールアドレスを入力してください。')
    .email('メールアドレスを正しい形式で入力してください'),
  password: yup
    .string()
    .required('パスワードを入力してください。')
    .test('password-complexity', 'パスワードの複雑さが不十分です', function (value) {
      const errors = passwordValidation(value);
      if (errors) {
        return this.createError({ message: errors.join('') });
      }
      return true;
    })
});

const passwordValidation = (value: string) => {
  const errors = [];

  // 8文字以上のチェック
  if (value.length < 8) {
    errors.push('パスワードは8文字以上を入力してください。');
  }
  if (!/[a-z]/.test(value)) {
    errors.push('小文字を1文字以上含めてください。');
  }
  if (!/[A-Z]/.test(value)) {
    errors.push('大文字を1文字以上含めてください。');
  }
  if (!/\d/.test(value)) {
    errors.push('数字を1文字以上含めてください。');
  }
  if (!/[\^$*.[\]{}()?\-"!@#%&/\\,><':;|_~`+=]/.test(value)) {
    errors.push('記号(^ $ * . [ ] { } ( ) ? - " ! @ # % & /  , > < \' : ; | _ ~ ` + =)を1文字以上含めてください。');
  }

  return errors.length ? errors : null;
};

export const LoginForm = (props: {
  setActivePage: React.Dispatch<React.SetStateAction<PageName>>;
  fromLine?: boolean;
}) => {
  const location = useLocation();
  awsconfig.oauth.redirectSignIn = pathOrigin + location.pathname;
  awsconfig.oauth.redirectSignOut = pathOrigin + location.pathname;
  Amplify.configure(awsconfig);
  const { handleSignIn } = useCognitoAuth();

  const {
    register,
    handleSubmit,
    getValues,
    formState: { errors },
    setError
  } = useForm<Pick<LoginFormType, 'email' | 'password'>>({
    defaultValues: {
      email: '',
      password: ''
    },
    resolver: yupResolver(schema)
  });

  const onSubmit = async () => {
    const mailaddress = getValues('email');
    const password = getValues('password');
    try {
      await handleSignIn(mailaddress, password);

      // LINEログイン後の遷移
      if (props.fromLine) {
        // クエリパラメータ "redirectUrl" に格納された、目的地のパスに遷移する
        const searchParams = new URLSearchParams(window.location.search);
        const redirectUrl = searchParams.get('redirectUrl');
        if (!redirectUrl) {
          window.location.href = ReservationUrls.Top;
          return;
        }
        return (window.location.href = redirectUrl);
      }

      // 非ログイン状態での予約フロー
      // ユーザーが予約可能日時を選択 → ログイン後、予約を確定するフロー
      // `redirectUrl` を使って、ユーザーが予約しようとしていた
      // 病院、メニュー、予約日時の予約確定ページにリダイレクトする処理
      const searchParams = new URLSearchParams(window.location.search);
      const redirectUrl = searchParams.get('redirectUrl');
      if (redirectUrl) {
        return (window.location.href = redirectUrl);
      }

      // 通常ログインの遷移
      const path = location.pathname;
      if (path === '/') {
        window.location.pathname = ReservationUrls.Top;
        return;
      }
      // path:/patient/mypageなど目的のページがある場合は、ログイン後目的のページに遷移させる
      return window.location.reload();
    } catch (error) {
      setError('root', {
        type: 'server',
        message: 'メールアドレスまたはパスワードが間違っています。再度ご確認ください。'
      });
    }
  };

  useEffect(() => {
    Hub.listen('auth', (data) => {
      try {
        switch (data.payload.event) {
          case 'signedIn':
          case 'signInWithRedirect':
            break;
          case 'signInWithRedirect_failure':
            console.log('Sign in failure');
            break;
          default:
            break;
        }
      } catch (error) {}
    });
  }, []);

  // LINEログイン機能は将来的に復帰する可能性があるため、コメントアウトで残しておく
  // const loginByLine = async () => {
  //   try {
  //     await signInWithRedirect({
  //       provider: { custom: 'LINE' }
  //     });
  //   } catch (error) {
  //     console.error(error);
  //     props.setActivePage(PageName.LoginError);
  //   }
  // };

  return (
    <>
      <Box sx={{ p: 2 }}>
        <Grid container direction={'row'} justifyContent={'center'} spacing={2}>
          <Grid item xs={12} sm={8}>
            <form onSubmit={handleSubmit(onSubmit)}>
              <Grid container direction={'column'} spacing={2}>
                {/* LINEログイン機能は将来的に復帰する可能性があるため、コメントアウトで残しておく */}
                {/* <Grid item xs={12} mb={3} mt={3}>
                  <Typography fontSize={14} mb={0.5}>
                    LINEで登録する方はこちら
                  </Typography>
                  <Button variant="line" size="lg" className="w-full" onClick={() => loginByLine()}>
                    <div>LINEでログイン</div>
                  </Button>
                </Grid> */}
                <TextInput
                  label={'メールアドレス'}
                  placeholder={'example@healthinterview.clinic'}
                  property={'email'}
                  errors={errors}
                  register={register}
                />
                <PasswordInput
                  label={'パスワード'}
                  placeholder={'8文字以上（大文字・小文字・数字・記号を含む）'}
                  property={'password'}
                  errors={errors}
                  register={register}
                />
                <Grid item sx={{ mt: 2 }}>
                  <Typography fontSize={14} className="text-right" sx={{ mb: 2 }}>
                    <Link sx={{ cursor: 'pointer' }} onClick={() => props.setActivePage(PageName.ResetPassword)}>
                      パスワードを忘れた方はこちら
                    </Link>
                  </Typography>
                  <Typography fontSize={14} className="text-right" sx={{ mb: 2 }}>
                    <Link sx={{ cursor: 'pointer' }} href="/recommended-browser">
                      推奨ブラウザはこちら
                    </Link>
                  </Typography>

                  {errors.root?.message && (
                    <Typography
                      color="error"
                      style={{ fontSize: '12px', marginLeft: '4px', marginBottom: '4px', textAlign: 'center' }}
                    >
                      {errors.root?.message}
                    </Typography>
                  )}

                  <Button type="submit" variant="main" size="lg" className="w-full">
                    <div>ログイン</div>
                  </Button>
                </Grid>
              </Grid>
            </form>
          </Grid>
        </Grid>
      </Box>
    </>
  );
};
